import { Frequency, RRule } from "rrule";

import type {
  AlertConfigurableFieldWithoutSectionTitle,
  AlertConfigurationMap,
  AlertSection,
  DeliveryChannelConfiguration,
  DueDateOption,
} from "./types";

export const ALERT_CONFIGURATIONS: AlertConfigurationMap = {
  credit: [
    {
      fieldName: "transactionQuery",
      fieldDefaultValue: 20000,
      fieldSectionTitle: "AMOUNT",
      fieldKey: "transaction-query",
      showSectionTitle: true,
      fieldLabel: "Alert me if a transaction is over",
    },
    {
      fieldName: "deliveryChannels",
      fieldSectionTitle: "ALERT DETAILS",
      fieldLabel: "",
      fieldKey: "delivery-channels",
      showSectionTitle: true,
      fieldDefaultValue: ["push", "sms", "email"],
    },
  ],
  debit: [
    {
      fieldName: "transactionQuery",
      fieldDefaultValue: 20000, // In cents
      fieldSectionTitle: "AMOUNT",
      fieldKey: "transaction-query",
      showSectionTitle: true,
      fieldLabel: "Alert me if a transaction is over",
    },
    {
      fieldName: "deliveryChannels",
      fieldSectionTitle: "ALERT DETAILS",
      fieldLabel: "",
      fieldKey: "delivery-channels",
      showSectionTitle: true,
      fieldDefaultValue: ["push", "sms", "email"],
    },
  ],
  low_available_balance: [
    {
      fieldName: "thresholdAmount",
      fieldDefaultValue: 50000, // In cents
      fieldSectionTitle: "AMOUNT",
      fieldKey: "threshold-amount",
      showSectionTitle: true,
      fieldLabel: "Alert me if my balance falls below",
    },
    {
      fieldName: "subscribedAccounts",
      fieldDefaultValue: [],
      showSectionTitle: true,
      fieldSectionTitle: "ALERT DETAILS",
      fieldKey: "subscribed-accounts",
      fieldLabel: "",
    },
    {
      fieldName: "deliveryChannels",
      fieldLabel: "",
      fieldKey: "delivery-channels",
      showSectionTitle: false,
      fieldDefaultValue: ["push", "sms", "email"],
    } as AlertConfigurableFieldWithoutSectionTitle,
  ],
  available_balance: [
    {
      fieldName: "rrule",
      // Defaults to every Monday
      fieldDefaultValue: new RRule({
        freq: Frequency.WEEKLY,
        byweekday: [RRule.MO],
      }).toString(),
      fieldSectionTitle: "DAYS ALERTED",
      fieldKey: "rrule",
      fieldLabel: "",
      showSectionTitle: true,
    },
    {
      fieldName: "subscribedAccounts",
      fieldDefaultValue: [],
      showSectionTitle: true,
      fieldSectionTitle: "ALERT DETAILS",
      fieldKey: "subscribed-accounts",
      fieldLabel: "",
    },
    {
      fieldName: "deliveryChannels",
      fieldLabel: "",
      showSectionTitle: false,
      fieldKey: "delivery-channels",
      fieldDefaultValue: ["push", "sms", "email"],
    } as AlertConfigurableFieldWithoutSectionTitle,
  ],
  loan_reminder: [
    {
      fieldName: "reminderDaysOffset",
      fieldDefaultValue: 0,
      showSectionTitle: true,
      fieldSectionTitle: "ALERT DETAILS",
      fieldKey: "reminder-days-offset",
      fieldLabel: "Remind me",
    },
    {
      fieldName: "deliveryChannels",
      fieldLabel: "",
      showSectionTitle: false,
      fieldKey: "delivery-channels",
      fieldDefaultValue: ["push", "sms", "email"],
    } as AlertConfigurableFieldWithoutSectionTitle,
  ],
  loan_overdue: [
    {
      fieldName: "deliveryChannels",
      fieldSectionTitle: "ALERT DETAILS",
      fieldLabel: "",
      fieldKey: "delivery-channels",
      showSectionTitle: true,
      fieldDefaultValue: ["push", "sms", "email"],
    },
  ],
};

export const LOAN_ACCOUNTS: readonly API.ProductType[] = [
  "loan",
  "line_of_credit",
  "credit_card",
  "mortgage",
  "lease",
] as const;

export const DUE_DATE_OPTIONS: DueDateOption[] = [
  {
    id: 0,
    value: "0",
    displayValue: "On the due date",
  },
  {
    id: 1,
    value: "1",
    displayValue: "1 day before",
  },
  {
    id: 2,
    value: "2",
    displayValue: "2 days before",
  },
  {
    id: 3,
    value: "3",
    displayValue: "3 days before",
  },
  {
    id: 4,
    value: "4",
    displayValue: "4 days before",
  },
  {
    id: 5,
    value: "5",
    displayValue: "5 days before",
  },
  {
    id: 6,
    value: "6",
    displayValue: "6 days before",
  },
  {
    id: 7,
    value: "7",
    displayValue: "7 days before",
  },
];

export const WEB_DELIVERY_CHANNELS: DeliveryChannelConfiguration[] = [
  {
    type: "sms",
    name: "SMS",
    icon: "tablet",
  },
  {
    type: "email",
    name: "Email",
    icon: "at-sign",
  },
];

export const MOBILE_DELIVERY_CHANNELS: DeliveryChannelConfiguration[] = [
  {
    name: "Push",
    type: "push",
    icon: "bell",
  },
  ...WEB_DELIVERY_CHANNELS,
];

export const ALERT_SECTIONS: AlertSection[] = [
  {
    section: "BALANCE ALERTS",
    sectionKey: "balance-alerts",
    items: [
      {
        key: "low_available_balance",
        isActive: false,
        alertName: "Balance below",
        configurableFields: true,
        navigateTo: "AlertRule",
      },
      {
        key: "available_balance",
        isActive: false,
        alertName: "Available balance",
        configurableFields: true,
        featureFlag: "periodic_alerts",
        navigateTo: "AlertRule",
      },
      {
        key: "credit",
        isActive: false,
        alertName: "Incoming transaction",
        configurableFields: true,
        navigateTo: "AlertRule",
      },
      {
        key: "loan_reminder",
        isActive: false,
        alertName: "Loan payment reminder",
        configurableFields: true,
        featureFlag: "periodic_alerts",
        navigateTo: "AlertRule",
      },
      {
        key: "loan_overdue",
        isActive: false,
        alertName: "Loan overdue",
        configurableFields: true,
        featureFlag: "periodic_alerts",
        navigateTo: "AlertRule",
      },
    ],
  },
  {
    section: "SPENDING ALERTS",
    sectionKey: "spending-alerts",
    items: [
      {
        key: "debit",
        isActive: false,
        alertName: "Incoming transaction",
        configurableFields: true,
        navigateTo: "AlertRule",
      },
    ],
  },
];
